import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Monitor, Building2, Trees, UtensilsCrossed, Wrench, ArrowUpRight, Cpu } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagementSystems = () => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', system_interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/system-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const systems = [
    {
      icon: <Building2 className="h-7 w-7" />,
      name: 'Quidity PMS',
      tag: 'Property Management',
      desc: 'Our proprietary property management system built for hotels, guest houses, and short term rental operators. Manage reservations, guests, housekeeping, and revenue from a single platform.',
      color: 'bg-blue-50 text-blue-700',
      badge: 'Built by Quidity',
    },
    {
      icon: <Trees className="h-7 w-7" />,
      name: 'Orchard LMS',
      tag: 'Landscape Management',
      desc: 'Purpose built for landscaping companies. Route optimization, crew scheduling, job costing, invoicing, and client management, all in one place.',
      color: 'bg-green-50 text-green-700',
      badge: 'Built by Quidity',
      link: 'https://orchardlms.com',
    },
    {
      icon: <UtensilsCrossed className="h-7 w-7" />,
      name: 'Clover POS',
      tag: 'Restaurant & Retail',
      desc: 'Industry leading point of sale for restaurants, retail, and service businesses. We\'re an authorized Clover dealer with setup, training, and ongoing support included.',
      color: 'bg-amber-50 text-amber-700',
      badge: 'Authorized Dealer',
    },
    {
      icon: <Wrench className="h-7 w-7" />,
      name: 'Field Service Management',
      tag: 'Service Businesses',
      desc: 'Scheduling, dispatching, invoicing, and customer management for HVAC, plumbing, electrical, and other field service companies.',
      color: 'bg-purple-50 text-purple-700',
      badge: 'Custom Solutions',
    },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 pt-20">
        <div className="max-w-md text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-[#115E59]" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Inquiry Received</h1>
          <p className="text-slate-500 mb-8">Thank you, {form.full_name}. Our team will review your inquiry and reach out to discuss the right solution for your business.</p>
          <Link to="/" className="text-sm font-semibold text-[#115E59] hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/5473955/pexels-photo-5473955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-indigo-950/80" />
        </div>
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-20 right-10 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
                <Cpu className="h-4 w-4 text-indigo-300" />
                <span className="text-xs font-semibold text-indigo-200 uppercase tracking-wider">Intelligent Systems</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Business<br />Management<br />
                <span className="text-indigo-300">Systems</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-md">
                Custom designed software, consulting, and industry leading platforms to streamline your operations and accelerate growth. Part of our Cashflow Engineering approach.
              </p>
              <a href="#inquiry" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                Request a Consultation <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            {/* Floating system cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {[
                { icon: <Building2 className="h-6 w-6" />, name: 'Property Management', label: 'Quidity PMS', color: 'text-blue-300' },
                { icon: <Trees className="h-6 w-6" />, name: 'Landscape Operations', label: 'Orchard LMS', color: 'text-green-300' },
                { icon: <UtensilsCrossed className="h-6 w-6" />, name: 'Restaurant & Retail', label: 'Clover POS', color: 'text-amber-300' },
                { icon: <Monitor className="h-6 w-6" />, name: 'Custom Software', label: 'Built for You', color: 'text-purple-300' },
              ].map((item, i) => (
                <div key={item.label} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className={`${item.color} mb-3`}>{item.icon}</div>
                  <div className="text-sm font-bold text-white mb-0.5">{item.label}</div>
                  <div className="text-xs text-slate-400">{item.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-700 mb-3 block">What We Do</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Design. Implement. Support.
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto mt-4">
              We don't just sell software. We analyze your operations, recommend the right system, customize it to your workflows, and provide ongoing support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Consult & Analyze', desc: 'We study your operations and identify inefficiencies, bottlenecks, and opportunities for automation.', icon: <Monitor className="h-6 w-6" /> },
              { step: '02', title: 'Design & Deploy', desc: 'Whether it\'s our own platform or a third-party system, we configure, customize, and launch it for your business.', icon: <Cpu className="h-6 w-6" /> },
              { step: '03', title: 'Train & Support', desc: 'Full training for your team and ongoing support to make sure the system keeps delivering results.', icon: <CheckCircle className="h-6 w-6" /> },
            ].map((s) => (
              <div key={s.step} className="text-center group">
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center mx-auto mb-6 group-hover:bg-indigo-100 transition-colors">{s.icon}</div>
                <div className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-2">Step {s.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Our Platforms</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Systems We Build & Support
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systems.map((sys) => (
              <div key={sys.name} className="bento-card bg-white overflow-hidden group">
                <div className="p-8 md:p-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-2xl ${sys.color} flex items-center justify-center`}>
                      {sys.icon}
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                      {sys.badge}
                    </span>
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{sys.tag}</div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{sys.name}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-6">{sys.desc}</p>
                  {sys.link ? (
                    <a
                      href={sys.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#115E59] hover:text-teal-700 transition-colors"
                    >
                      Visit {sys.name} <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <a
                      href="#inquiry"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#115E59] hover:text-teal-700 transition-colors"
                    >
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-slate-900 text-white relative noise-overlay overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-400 mb-4 block">Industries</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              We Serve Businesses Like Yours
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Hotels & Resorts',
              'Guest Houses & B&Bs',
              'Landscaping Companies',
              'Restaurants & Cafes',
              'Retail Stores',
              'Field Service Companies',
              'Property Managers',
              'And More...',
            ].map((ind) => (
              <div key={ind} className="bg-white/[0.05] border border-white/10 rounded-xl p-5 text-center hover:bg-white/10 transition-colors">
                <span className="text-sm font-semibold text-slate-200">{ind}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry" className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-4 block">Get Started</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Request a Consultation
            </h2>
            <p className="text-slate-500 max-w-lg mx-auto">
              Tell us about your business and what you're looking for. Our team will reach out to discuss the right solution.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="system-inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Full Name *</label>
                <input data-testid="system-full-name" name="full_name" required value={form.full_name} onChange={handleChange}
                  className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Email *</label>
                <input data-testid="system-email" name="email" type="email" required value={form.email} onChange={handleChange}
                  className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="john@company.com" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Phone *</label>
                <input data-testid="system-phone" name="phone" type="tel" required value={form.phone} onChange={handleChange}
                  className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="(555) 123-4567" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company</label>
                <input data-testid="system-company" name="company" value={form.company} onChange={handleChange}
                  className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="Your Business" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">What are you interested in?</label>
              <select data-testid="system-interest" name="system_interest" value={form.system_interest} onChange={handleChange}
                className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all">
                <option value="">Select a system...</option>
                <option value="Quidity PMS">Quidity PMS, Property Management</option>
                <option value="Orchard LMS">Orchard LMS, Landscape Management</option>
                <option value="Clover POS">Clover POS, Restaurant and Retail</option>
                <option value="Field Service">Field Service Management</option>
                <option value="Custom Software">Custom Software Design</option>
                <option value="Consulting">General Consulting</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tell us about your needs</label>
              <textarea data-testid="system-message" name="message" rows={4} value={form.message} onChange={handleChange}
                className="w-full bg-[#FAFAFA] border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all resize-none" placeholder="Describe your business, current systems, and what you're looking to improve..." />
            </div>

            {error && <div data-testid="system-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</div>}

            <button
              data-testid="system-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white font-semibold text-sm py-4 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Request a Consultation'}
            </button>
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/5473955/pexels-photo-5473955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Smarter Systems,<br />Stronger Business
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            The right management system doesn't just organize your business, it transforms it. Let's find yours.
          </p>
          <a href="#inquiry" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-8 py-4 rounded-full hover:bg-slate-100 transition-colors">
            Get a Consultation <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ManagementSystems;
